'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Award, Shield, Mail, CheckCircle, Star, Handshake, Settings, Users, Wrench, TrendingUp, BadgeCheck, Building2, ChevronLeft, Target, Rocket } from 'lucide-react';

const stats = [
  { value: '1.000+', label: 'Reparaturen', sub: 'erfolgreich abgeschlossen' },
  { value: '4,9 / 5', label: 'Bewertung', sub: 'von unseren Kunden' },
  { value: '12 Mon.', label: 'Garantie', sub: 'auf alle Reparaturen' },
  { value: '2–7 Tage', label: 'Bearbeitung', sub: 'schnelle Abwicklung' },
];

const milestones = [
  {
    year: '2022',
    title: 'Gründung',
    desc: 'Media Rabbit GmbH wird gegründet – mit dem Ziel, professionelle Reparaturen für Spielekonsolen, PCs und mehr für Endkunden und Händler anzubieten.',
  },
  {
    year: '2023',
    title: 'Wachstum & Ausbau',
    desc: 'Erweiterung des Serviceangebots auf weitere Konsolenmarken und erste Schritte in Richtung PC-Reparaturen.',
  },
  {
    year: '2024',
    title: 'B2B-Partnerschaften',
    desc: 'Erste strategische Kooperationen mit Elektronikhändlern und Unternehmenskunden in Deutschland.',
  },
  {
    year: '2025',
    title: '1.000+ Reparaturen',
    desc: 'Über tausend abgeschlossene Reparaturen und ein wachsendes Netzwerk an Geschäftspartnern bundesweit.',
  },
];

const visionGoals = [
  {
    icon: Target,
    title: 'Direktes Reparatur-Booking',
    desc: 'Kunden sollen bald direkt online einen Reparaturtermin buchen können – schnell, einfach und ohne Umwege.',
    color: 'text-primary',
    bg: 'bg-primary/8',
  },
  {
    icon: Rocket,
    title: 'Erweiterung des Serviceangebots',
    desc: 'Wir planen die Ausweitung unserer Reparaturen auf Mac, PC, Drohnen und viele weitere Gerätekategorien.',
    color: 'text-accent',
    bg: 'bg-accent/8',
  },
  {
    icon: TrendingUp,
    title: 'Bundesweites Netzwerk',
    desc: 'Aufbau eines flächendeckenden Partnernetzwerks für noch schnellere Abwicklung in ganz Deutschland.',
    color: 'text-success',
    bg: 'bg-success/8',
  },
];

const certifications = [
  {
    icon: BadgeCheck,
    title: 'Zertifizierter Fachbetrieb',
    desc: 'Unsere Techniker sind geschult und zertifiziert für die Reparatur aller gängigen Spielekonsolen.',
    color: 'text-primary',
    bg: 'bg-primary/8',
  },
  {
    icon: Shield,
    title: 'Qualitätsgesicherte Ersatzteile',
    desc: 'Wir verwenden ausschließlich geprüfte Originalteile und hochwertige Ersatzkomponenten.',
    color: 'text-success',
    bg: 'bg-success/8',
  },
  {
    icon: Award,
    title: 'Eingetragenes Unternehmen',
    desc: 'Media Rabbit GmbH ist ein eingetragenes Unternehmen mit Sitz in Deutschland.',
    color: 'text-accent',
    bg: 'bg-accent/8',
  },
  {
    icon: TrendingUp,
    title: 'Kontinuierliche Weiterbildung',
    desc: 'Unser Team bildet sich regelmäßig weiter, um stets auf dem neuesten Stand der Technik zu sein.',
    color: 'text-warning',
    bg: 'bg-warning/8',
  },
];

const b2bBenefits = [
  'Individuelle Rahmenverträge & Konditionen',
  'Priorisierte Bearbeitung für Geschäftskunden',
  'Dedizierter Ansprechpartner',
  'Sammelabrechnung & Monatsrechnung',
  'Abholung & Lieferung auf Anfrage',
  'Technische Beratung & Diagnose',
];

export default function UeberUnsClient() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Image
                src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
                alt="Media Rabbit GmbH Logo"
                width={120}
                height={40}
                className="h-9 w-auto object-contain"
                priority
              />
            </div>
            <nav className="hidden md:flex items-center gap-1">
              <Link href="/#preisliste" className="btn-ghost text-sm font-medium">Preisliste</Link>
              <Link href="/reparatur-anfrage" className="btn-ghost text-sm font-medium">Anfrage</Link>
              <Link href="/ueber-uns" className="btn-ghost text-sm font-medium text-primary">Über uns</Link>
              <Link href="/#kontakt" className="btn-ghost text-sm font-medium">Kontakt</Link>
              <Link href="/#faq" className="btn-ghost text-sm font-medium">FAQ</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Link href="/reparatur-anfrage" className="hidden sm:flex btn-primary items-center gap-2 text-sm px-4 py-2">
                <Mail size={14} />
                Reparatur anfragen
              </Link>
              <Link
                href="/admin-login"
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-xs px-2 py-1 rounded border border-transparent hover:border-border"
              >
                <Settings size={12} />
                <span className="hidden sm:inline">Admin</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
      {/* Hero */}
      <section className="relative overflow-hidden hero-glow grid-pattern">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-12">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors text-sm mb-8"
          >
            <ChevronLeft size={16} />
            Zurück zur Startseite
          </Link>
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-full px-4 py-1.5 mb-5">
              <Building2 size={14} className="text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wide">Media Rabbit GmbH</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-5 leading-tight">
              Unsere{' '}
              <span className="text-gradient-primary">Geschichte & Expertise</span>
            </h1>
            <p className="text-secondary-foreground text-lg max-w-xl mx-auto leading-relaxed">
              Gegründet 2022 – mit über 25 Jahren Reparaturerfahrung im Rücken. Wir sind der vertrauenswürdige
              Partner für professionelle Reparaturen von PCs, Spielekonsolen und mehr – für Privatkunden
              und Unternehmen in ganz Deutschland.
            </p>
          </div>
        </div>
      </section>
      {/* Stats */}
      <section className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats?.map(stat => (
            <div key={stat?.label} className="card-surface p-6 shadow-card text-center">
              <div className="text-3xl font-extrabold text-primary mb-1 tabular-nums">{stat?.value}</div>
              <div className="text-foreground font-semibold text-sm mb-0.5">{stat?.label}</div>
              <div className="text-muted-foreground text-xs">{stat?.sub}</div>
            </div>
          ))}
        </div>
      </section>
      {/* Our Story */}
      <section className="bg-white border-y border-border py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                <Wrench size={22} className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Unsere Geschichte</h2>
            </div>
            <p className="text-muted-foreground text-base leading-relaxed mb-5">
              Media Rabbit GmbH wurde 2022 gegründet – mit einer klaren Mission: Reparaturen sollen
              transparent, fair und professionell sein. Was als spezialisierter Betrieb für Spielekonsolen
              und PCs begann, hat sich zu einem verlässlichen Partner für Privatkunden und Unternehmen
              in ganz Deutschland entwickelt.
            </p>
            <p className="text-muted-foreground text-base leading-relaxed mb-10">
              Hinter Media Rabbit steckt mehr als 25 Jahre Reparaturerfahrung. Unser Team repariert täglich
              PCs, Spielekonsolen aller gängigen Marken und vieles mehr – mit Festpreisen, kurzen
              Bearbeitungszeiten und einer Garantie, die wir wirklich einhalten. Wir glauben daran,
              dass gute Technik ein zweites Leben verdient.
            </p>

            {/* Timeline */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {milestones?.map((m) => (
                <div key={m?.year} className="flex gap-4 p-5 rounded-xl border border-border bg-background">
                  <div className="shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold text-sm">{m?.year}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold text-sm mb-1">{m?.title}</h3>
                    <p className="text-muted-foreground text-xs leading-relaxed">{m?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Team */}
      <section className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Users size={22} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Unser Team</h2>
          </div>
          <p className="text-muted-foreground text-base leading-relaxed mb-8">
            Hinter Media Rabbit stehen leidenschaftliche Techniker mit jahrelanger Erfahrung in der
            Elektronikreparatur. Jedes Gerät wird von Hand geprüft, diagnostiziert und mit größter
            Sorgfalt repariert.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                role: 'Technischer Leiter',
                focus: 'PlayStation & Xbox Spezialist',
                exp: '8+ Jahre Erfahrung',
                icon: Wrench,
              },
              {
                role: 'Servicetechniker',
                focus: 'Nintendo & Handheld Geräte',
                exp: '5+ Jahre Erfahrung',
                icon: Settings,
              },
              {
                role: 'B2B-Koordinator',
                focus: 'Geschäftskunden & Partnerschaften',
                exp: '6+ Jahre Erfahrung',
                icon: Handshake,
              },
            ]?.map(member => (
              <div key={member?.role} className="card-surface p-6 shadow-card text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <member.icon size={24} className="text-primary" />
                </div>
                <div className="text-foreground font-semibold text-sm mb-1">{member?.role}</div>
                <div className="text-muted-foreground text-xs mb-2">{member?.focus}</div>
                <div className="inline-flex items-center gap-1 bg-success/10 text-success text-xs font-medium px-2.5 py-1 rounded-full">
                  <CheckCircle size={11} />
                  {member?.exp}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Certifications */}
      <section className="bg-white border-y border-border py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                <Award size={22} className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Zertifizierungen & Qualität</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {certifications?.map(cert => (
                <div key={cert?.title} className="flex gap-4 p-5 rounded-xl border border-border bg-background">
                  <div className={`shrink-0 w-11 h-11 rounded-xl ${cert?.bg} flex items-center justify-center`}>
                    <cert.icon size={20} className={cert?.color} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold text-sm mb-1">{cert?.title}</h3>
                    <p className="text-muted-foreground text-xs leading-relaxed">{cert?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Vision & Goals */}
      <section className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Rocket size={22} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Unsere Ziele & Vision</h2>
          </div>
          <p className="text-muted-foreground text-base leading-relaxed mb-8">
            Wir stehen erst am Anfang. Media Rabbit hat große Pläne – für unsere Kunden, unser
            Serviceangebot und die Art, wie Reparaturen in Deutschland funktionieren.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {visionGoals?.map(goal => (
              <div key={goal?.title} className="flex flex-col gap-3 p-5 rounded-xl border border-border bg-background">
                <div className={`w-11 h-11 rounded-xl ${goal?.bg} flex items-center justify-center`}>
                  <goal.icon size={20} className={goal?.color} />
                </div>
                <div>
                  <h3 className="text-foreground font-semibold text-sm mb-1">{goal?.title}</h3>
                  <p className="text-muted-foreground text-xs leading-relaxed">{goal?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* B2B Section */}
      <section className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Building2 size={22} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">B2B-Expertise & Partnerschaften</h2>
          </div>
          <p className="text-muted-foreground text-base leading-relaxed mb-8">
            Wir sind ein verlässlicher Partner für Elektronikhändler, Versicherungen und Unternehmen,
            die regelmäßig Konsolenreparaturen benötigen. Unsere B2B-Kunden profitieren von
            maßgeschneiderten Konditionen und einem dedizierten Service.
          </p>

          <div className="card-surface p-6 shadow-card mb-8">
            <h3 className="text-foreground font-semibold text-base mb-4 flex items-center gap-2">
              <Star size={16} className="text-warning" />
              Ihre Vorteile als Geschäftskunde
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {b2bBenefits?.map(benefit => (
                <div key={benefit} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle size={15} className="text-success shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:info@mediarabbit.de"
              className="btn-primary flex items-center justify-center gap-2 text-sm px-6 py-3"
            >
              <Mail size={16} />
              B2B-Anfrage stellen
            </a>
            <Link
              href="/reparatur-anfrage"
              className="btn-secondary flex items-center justify-center gap-2 text-sm px-6 py-3"
            >
              <Wrench size={16} />
              Reparatur anfragen
            </Link>
          </div>
        </div>
      </section>
      {/* Dark CTA */}
      <section className="bg-foreground text-white py-14">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
              <Handshake size={14} className="text-white/80" />
              <span className="text-white/80 text-xs font-semibold tracking-wide">Für Unternehmen & Händler</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Bereit für eine Zusammenarbeit?</h2>
            <p className="text-white/70 text-base leading-relaxed mb-6 max-w-lg mx-auto">
              Kontaktieren Sie uns für ein unverbindliches Gespräch über individuelle Konditionen
              und maßgeschneiderte Servicelösungen für Ihr Unternehmen.
            </p>
            <a
              href="mailto:info@mediarabbit.de"
              className="inline-flex items-center gap-2 bg-white text-foreground font-semibold px-6 py-3 rounded-lg hover:bg-white/90 transition-colors text-sm"
            >
              <Mail size={16} />
              info@mediarabbit.de
            </a>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-border bg-white">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Image
                src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
                alt="Media Rabbit GmbH"
                width={90}
                height={30}
                className="h-7 w-auto object-contain"
              />
              <span className="text-muted-foreground text-sm">© 2026 Media Rabbit GmbH</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <a href="mailto:info@mediarabbit.de" className="hover:text-foreground transition-colors">Kontakt</a>
              <Link href="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
              <Link href="/admin-login" className="hover:text-foreground transition-colors text-xs opacity-40">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
